import { NextActionExecutionHistory, NextActionExecutionHistoryItem } from '@liveperson/lpcontextschemalib';
import React, {
  useEffect, useRef,
} from 'react';
import {
  List, AutoSizer, CellMeasurerCache, CellMeasurer, ListRowProps,
} from 'react-virtualized';
import { Box } from 'rebass/styled-components';
import { PolicyExecution } from '.';
import useAppState from '../../hooks/app-state.hook';
import '../../styles/styles.css';

const RENDER_INCREMENT = 10;

export interface INextActionExecutionListProps {
  list: Array<NextActionExecutionHistoryItem>;
  execution: NextActionExecutionHistory;
}

const NextActionExecutionList: React.FC<INextActionExecutionListProps> = ({
  list, execution,
}: INextActionExecutionListProps) => {
  const cache = useRef<any>(new CellMeasurerCache({
    defaultHeight: 38,
    fixedWidth: true,
  }));

  const isMounted = useRef<boolean>(false);
  const { themeMode } = useAppState();
  const historyLength = list?.length || 0;

  function rowRenderer({
    key, // Unique key within array of rows
    index, // Index of row within collection
    style, // Style object to be applied to row (to position it)
    parent,
  }: ListRowProps) {
    const policyRun: NextActionExecutionHistoryItem = list[index];
    const keyVal = `${execution.timestamp}-${policyRun.policy?.docId}`;

    return (
      <CellMeasurer
        cache={cache.current}
        columnIndex={0}
        key={key}
        overscanRowCount={RENDER_INCREMENT}
        parent={parent}
        rowIndex={index}
      >
        {({ registerChild, measure }) => (
          <Box key={keyVal} ref={registerChild} style={style}>
            <PolicyExecution
              policyRun={policyRun}
              policyVariables={execution.executionResult?.variables}
              policyExceptions={execution.executionResult?.exceptions}
              isFirst={index === 0}
              isLast={index === historyLength - 1}
              key={policyRun.policy?.docId || index + 1}
              measure={measure}
            />
          </Box>
        )}
      </CellMeasurer>
    );
  }

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Box data-testid="next-action-execution-list" height={`${list.length * 55}px`} minHeight="55px" maxHeight="400px">
      <AutoSizer>
        {({ height, width }) => (
          <List
            class={themeMode === 'light' ? null : 'dark-theme'}
            height={height || 400}
            rowCount={list.length}
            rowRenderer={rowRenderer}
            width={width || 800}
            deferredMeasurementCache={cache.current}
            rowHeight={cache.current.rowHeight}
          />
        )}
      </AutoSizer>
    </Box>

  );
};

export default NextActionExecutionList;
